import type { TFunction } from "@/i18n/catalog";

/**
 * L1 diagnostic reasons — why an event stayed unidentified (tier L1). Keyed by
 * `l1_diagnostic_reasons.id` (the `by_l1_reason` stats bucket key). Mirrors the seeded reference
 * table; the wording lives in the catalogue so it is editable without a migration (POS2-755), and
 * translated because the L1 breakdown widget is a customer-facing dashboard panel (POS2-1134).
 *
 * Literal keys in a lookup table, never `t(`reference.l1Reason.${id}.label`)` — a key assembled at
 * runtime is invisible to `check-i18n.mjs` and would be reported as dead.
 */
const L1_REASON_KEYS: Record<string, { label: string; action: string; description: string }> = {
  obstructed_poor_image: {
    label: "reference.l1Reason.obstructed_poor_image.label",
    action: "reference.l1Reason.obstructed_poor_image.action",
    description: "reference.l1Reason.obstructed_poor_image.description",
  },
  image_unreadable: {
    label: "reference.l1Reason.image_unreadable.label",
    action: "reference.l1Reason.image_unreadable.action",
    description: "reference.l1Reason.image_unreadable.description",
  },
  mixed_contents: {
    label: "reference.l1Reason.mixed_contents.label",
    action: "reference.l1Reason.mixed_contents.action",
    description: "reference.l1Reason.mixed_contents.description",
  },
  multiple_items: {
    label: "reference.l1Reason.multiple_items.label",
    action: "reference.l1Reason.multiple_items.action",
    description: "reference.l1Reason.multiple_items.description",
  },
  non_food_contamination: {
    label: "reference.l1Reason.non_food_contamination.label",
    action: "reference.l1Reason.non_food_contamination.action",
    description: "reference.l1Reason.non_food_contamination.description",
  },
  insufficient_content: {
    label: "reference.l1Reason.insufficient_content.label",
    action: "reference.l1Reason.insufficient_content.action",
    description: "reference.l1Reason.insufficient_content.description",
  },
  surface_mismatch: {
    label: "reference.l1Reason.surface_mismatch.label",
    action: "reference.l1Reason.surface_mismatch.action",
    description: "reference.l1Reason.surface_mismatch.description",
  },
  before_image_unavailable: {
    label: "reference.l1Reason.before_image_unavailable.label",
    action: "reference.l1Reason.before_image_unavailable.action",
    description: "reference.l1Reason.before_image_unavailable.description",
  },
  bin_not_inspectable: {
    label: "reference.l1Reason.bin_not_inspectable.label",
    action: "reference.l1Reason.bin_not_inspectable.action",
    description: "reference.l1Reason.bin_not_inspectable.description",
  },
  bin_empty: {
    label: "reference.l1Reason.bin_empty.label",
    action: "reference.l1Reason.bin_empty.action",
    description: "reference.l1Reason.bin_empty.description",
  },
  empty_surface: {
    label: "reference.l1Reason.empty_surface.label",
    action: "reference.l1Reason.empty_surface.action",
    description: "reference.l1Reason.empty_surface.description",
  },
};

/** Every reason id the widget may receive, in the order the reference table seeds them. */
export const L1_REASON_IDS = Object.keys(L1_REASON_KEYS);

/** Human label for an L1 reason id, falling back to the raw id. */
export function l1ReasonLabel(t: TFunction, id: string): string {
  const keys = L1_REASON_KEYS[id];
  return keys ? t(keys.label) : id;
}

/** Action suggestion for an L1 reason id, or null when unknown. */
export function l1ReasonAction(t: TFunction, id: string): string | null {
  const keys = L1_REASON_KEYS[id];
  return keys ? t(keys.action) : null;
}

/**
 * The full sentence for an L1 reason id, falling back to what the API sent (POS2-1267).
 *
 * The fallback is the English `l1_diagnostic_reasons.description`, which is what the card rendered
 * unconditionally before. `l1_diagnostic_reasons` is seeded, so a migration can add a row before
 * the catalogue has a key for it — and an untranslated sentence beats an empty grey box.
 */
export function l1ReasonDescription(t: TFunction, id: string, fallback: string): string {
  const keys = L1_REASON_KEYS[id];
  return keys ? t(keys.description) : fallback;
}
